import type { ReactNode } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import { SignatureConfirmPanel } from './SignatureConfirmPanel'

/**
 * The signature step, as a dialog of its own.
 *
 * Every signed action a process step offers — Start, Stop, Complete, Undo
 * start — comes through here. It used to be a panel appended BELOW the working
 * screen, which meant the thing being confirmed sat underneath the thing it was
 * confirming, and on a long record the operator had to scroll past the lot
 * lists to find it. A confirmation that appears under the work does not read as
 * a confirmation.
 *
 * Rendered as a SIBLING of the step dialog rather than inside its content.
 * Both portal to the body, so the DOM result is identical, but keeping them
 * un-nested in the React tree avoids the focus-trap fight that made an earlier
 * attempt at this unusable. Closing this one returns to the step dialog, which
 * has stayed open and unchanged underneath.
 */
export function ProcessSignDialog({
  open,
  title,
  description,
  children,
  meaning,
  signerName,
  password,
  onPasswordChange,
  confirmLabel,
  confirmDisabled,
  tone,
  busy,
  error,
  onConfirm,
  onCancel,
  signerPicker,
  signingForOther,
}: {
  open: boolean
  title: string
  description?: string
  /** What is being attested — the summary, or the undo reason field. */
  children?: ReactNode
  meaning: string
  signerName: string
  password: string
  onPasswordChange: (value: string) => void
  confirmLabel: string
  confirmDisabled: boolean
  /** Follows the transition, so the same action wears one colour throughout. */
  tone: 'primary' | 'red' | 'green'
  busy: boolean
  error: string | null
  onConfirm: () => void
  onCancel: () => void
  /** "Sign as" picker, passed through to the signature panel. */
  signerPicker?: ReactNode
  signingForOther?: boolean
}) {
  return (
    <Dialog
      open={open}
      // Escape and the backdrop mean "not now", never "throw the record away":
      // this closes and the step dialog is still there, exactly as it was.
      onOpenChange={o => {
        if (!o && !busy) onCancel()
      }}
    >
      <DialogContent className='max-h-[90vh] overflow-y-auto sm:max-w-lg'>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && (
            <p className='text-sm text-muted-foreground'>{description}</p>
          )}
        </DialogHeader>

        <div className='space-y-4 py-1'>
          {children}
          <SignatureConfirmPanel
            meaning={meaning}
            signerName={signerName}
            value={password}
            onChange={onPasswordChange}
            onSubmit={onConfirm}
            signerPicker={signerPicker}
            signingForOther={signingForOther}
          />
          {error && <p className='text-xs font-medium text-red-600'>{error}</p>}
        </div>

        <DialogFooter>
          <Button type='button' variant='outline' onClick={onCancel} disabled={busy}>
            Back
          </Button>
          <Button
            type='button'
            disabled={confirmDisabled || busy}
            onClick={onConfirm}
            className={cn(
              'font-semibold text-white',
              tone === 'red'
                ? 'bg-red-600 hover:bg-red-700'
                : tone === 'green'
                  ? 'bg-green-600 hover:bg-green-700'
                  : 'bg-primary hover:bg-primary/90',
            )}
          >
            {busy ? 'Verifying…' : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
